import {
  Permission,
  WorkspaceMemberStatus,
  type WorkspaceMemberWithUserDto,
  type WorkspaceRoleListItemDto,
} from '@up2stay/up2promo-api-client';

import { getMemberDisplayName } from '@/core/components/leads/lead-detail/lead-user-display';

const DEFAULT_NAME_CAP = 4;

export type AssigneeLockTooltipLabels = {
  /** Fallback when no names can be resolved (permission prose). */
  lockedTooltip: string;
  /** `{names}` = comma-separated names; used when ≤ cap. */
  lockedTooltipNamed: string;
  /** `{names}` + `{count}` more; used when overflowing the cap. */
  lockedTooltipNamedMore: string;
};

/**
 * Best-effort list of people who can change assignee:
 * members whose role grants LEADS_UPDATE (and LEADS_UPDATE_OTHERS for
 * others’ private leads), plus the lead owner. Caps the named list.
 */
export function listAssigneeEditors(options: {
  members: readonly WorkspaceMemberWithUserDto[];
  roles: readonly WorkspaceRoleListItemDto[];
  ownerUserId: string;
  isPrivate: boolean;
  /** When true, private lead is someone else’s → also need UPDATE_OTHERS. */
  isOthersPrivate: boolean;
}): string[] {
  const ownerTrim = options.ownerUserId.trim();
  const permsByRoleKey = new Map<string, ReadonlySet<string>>();

  for (const role of options.roles) {
    const key = role.builtinRole
      ? `builtin:${role.builtinRole}`
      : `custom:${role.id}`;
    permsByRoleKey.set(key, new Set(role.permissions ?? []));
  }

  const names: string[] = [];
  const seen = new Set<string>();

  const pushName = (userId: string, label: string) => {
    const id = userId.trim();
    if (!id || seen.has(id)) return;
    seen.add(id);
    const name = label.trim() || id;
    names.push(name);
  };

  for (const member of options.members) {
    if (member.status !== WorkspaceMemberStatus.ACTIVE) continue;
    const uid = member.userId?.trim();
    if (!uid) continue;

    const roleKey = member.customRoleId?.trim()
      ? `custom:${member.customRoleId.trim()}`
      : `builtin:${member.role}`;
    const perms = permsByRoleKey.get(roleKey);
    if (!perms?.has(Permission.LEADS_UPDATE)) continue;
    if (options.isOthersPrivate && !perms.has(Permission.LEADS_UPDATE_OTHERS)) {
      continue;
    }

    pushName(uid, getMemberDisplayName(member) || uid);
  }

  // Owner can always manage own leads when they hold LEADS_UPDATE via their
  // role; if they were filtered out (e.g. stripped role), still surface them.
  if (ownerTrim && !seen.has(ownerTrim)) {
    const ownerMember = options.members.find(
      (m) => m.userId?.trim() === ownerTrim,
    );
    if (ownerMember?.status === WorkspaceMemberStatus.ACTIVE) {
      pushName(ownerTrim, getMemberDisplayName(ownerMember) || ownerTrim);
    }
  }

  return names;
}

export function formatAssigneeLockTooltip(
  names: readonly string[],
  labels: AssigneeLockTooltipLabels,
  cap: number = DEFAULT_NAME_CAP,
): string {
  if (names.length === 0) return labels.lockedTooltip;
  if (names.length <= cap) {
    return labels.lockedTooltipNamed.replace('{names}', names.join(', '));
  }
  const shown = names.slice(0, cap).join(', ');
  const remaining = names.length - cap;
  return labels.lockedTooltipNamedMore
    .replace('{names}', shown)
    .replace('{count}', String(remaining));
}
